/**
 * Query Management Page
 * Communication channel between admin and users
 *
 * - Admin can raise queries targeting a specific user or role
 * - Targeted users can respond with messages (threaded)
 * - Admin can close queries
 * - All actions logged to audit trail
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Clock,
  Send,
  X,
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  User as UserIcon,
} from 'lucide-react';

interface QueryMessage {
  text: string;
  authorUsername: string;
  authorRole: string;
  createdAt: string;
}

interface QueryItem {
  _id: string;
  queryId: string;
  title: string;
  status: 'open' | 'answered' | 'closed';
  priority: 'low' | 'medium' | 'high';
  targetUser?: { _id: string; username: string; fullName: string };
  targetRole?: string;
  subject?: { displayId: string; subjectId: string };
  site?: { siteId: string; siteName: string };
  formId?: string;
  fieldId?: string;
  messages: QueryMessage[];
  raisedByUsername: string;
  raisedAt: string;
  closedByUsername?: string;
  closedAt?: string;
  closeReason?: string;
}

interface StudyUser {
  _id: string;
  username: string;
  fullName: string;
  role: string;
}

const roleLabels: Record<string, string> = {
  'site-rc': 'Site Research Coordinators',
  site: 'Site Users',
  analyst: 'Core Lab Analysts',
  cec: 'CEC Members',
};

const QueryManagement: React.FC = () => {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const role = user?.role || 'site-rc';
  const isAdmin = role === 'admin';

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null);

  // New query modal state
  const [showNewQuery, setShowNewQuery] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newPriority, setNewPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [newTargetType, setNewTargetType] = useState<'user' | 'role'>('user');
  const [newTargetUserId, setNewTargetUserId] = useState('');
  const [newTargetRole, setNewTargetRole] = useState('');
  const [newSubjectId, setNewSubjectId] = useState('');

  // Reply state
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  // Close modal state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingQuery, setClosingQuery] = useState<QueryItem | null>(null);
  const [closeReason, setCloseReason] = useState('');

  // Fetch ALL queries (filter client-side so counters stay accurate)
  const { data, isLoading } = useQuery({
    queryKey: ['queries'],
    queryFn: async () => {
      const res = await fetch(`/api/queries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Fetch users (for admin's target selector)
  const { data: usersData } = useQuery({
    queryKey: ['study-users'],
    queryFn: async () => {
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return { users: [] };
      return res.json();
    },
    enabled: isAdmin,
  });

  const studyUsers: StudyUser[] = usersData?.users || [];

  // Fetch subjects for the selected target user/role
  const targetKey = newTargetType === 'user' ? newTargetUserId : newTargetRole;
  const { data: targetSubjectsData } = useQuery({
    queryKey: ['target-subjects', newTargetType, targetKey],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (newTargetType === 'user' && newTargetUserId) params.set('targetUserId', newTargetUserId);
      if (newTargetType === 'role' && newTargetRole) params.set('targetRole', newTargetRole);
      const res = await fetch(`/api/queries/subjects-for-target?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return { subjects: [] };
      return res.json();
    },
    enabled: isAdmin && showNewQuery && !!targetKey,
  });

  const targetSubjects: { _id: string; displayId: string; site?: { siteId: string; siteName: string } }[] = targetSubjectsData?.subjects || [];

  // Create query mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Failed to create query');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queries'] });
      setShowNewQuery(false);
      setNewTitle('');
      setNewMessage('');
      setNewPriority('medium');
      setNewTargetUserId('');
      setNewTargetRole('');
      setNewSubjectId('');
    },
  });

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: async ({ queryId, text }: { queryId: string; text: string }) => {
      const res = await fetch(`/api/queries/${queryId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Failed to send reply');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queries'] });
      setReplyText('');
      setReplyingTo(null);
    },
  });

  // Close mutation
  const closeMutation = useMutation({
    mutationFn: async ({ queryId, closeReason }: { queryId: string; closeReason: string }) => {
      const res = await fetch(`/api/queries/${queryId}/close`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ closeReason }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Failed to close query');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queries'] });
      setShowCloseModal(false);
      setClosingQuery(null);
      setCloseReason('');
    },
  });

  const queries: QueryItem[] = data?.queries || [];

  // Counts always from full list (not filtered by tab)
  const openCount = queries.filter(q => q.status === 'open').length;
  const answeredCount = queries.filter(q => q.status === 'answered').length;
  const closedCount = queries.filter(q => q.status === 'closed').length;

  // Filter by status tab + search term
  const filteredQueries = queries.filter(q => {
    // Status filter
    if (statusFilter !== 'all' && q.status !== statusFilter) return false;
    // Search filter
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (
        q.title?.toLowerCase().includes(s) ||
        q.targetUser?.fullName?.toLowerCase().includes(s) ||
        q.targetUser?.username?.toLowerCase().includes(s) ||
        q.targetRole?.toLowerCase().includes(s) ||
        q.raisedByUsername?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-amber-100 text-amber-700';
      case 'answered': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleBadge = (r: string) => {
    switch (r) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'site-rc': return 'bg-blue-100 text-blue-700';
      case 'site': return 'bg-indigo-100 text-indigo-700';
      case 'analyst': return 'bg-green-100 text-green-700';
      case 'cec': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTargetLabel = (q: QueryItem) => {
    if (q.targetUser) return q.targetUser.fullName || q.targetUser.username;
    if (q.targetRole) return roleLabels[q.targetRole] || q.targetRole;
    return 'All';
  };

  const canReply = (q: QueryItem) => q.status !== 'closed';
  const canClose = (q: QueryItem) => isAdmin && q.status !== 'closed';

  const handleCreateQuery = () => {
    const payload: any = {
      title: newTitle,
      message: newMessage || undefined,
      priority: newPriority,
      subjectId: newSubjectId || undefined,
    };
    if (newTargetType === 'user' && newTargetUserId) {
      payload.targetUserId = newTargetUserId;
    } else if (newTargetType === 'role' && newTargetRole) {
      payload.targetRole = newTargetRole;
    }
    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Queries</h1>
          <p className="text-gray-600">
            {isAdmin ? 'Raise, review, and close queries with users' : 'View and respond to queries'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowNewQuery(true)}
            className="flex items-center gap-2 px-4 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600"
          >
            <Plus size={18} />
            New Query
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Open', count: openCount, status: 'open', icon: Clock },
          { label: 'Answered', count: answeredCount, status: 'answered', icon: MessageSquare },
          { label: 'Closed', count: closedCount, status: 'closed', icon: CheckCircle },
        ].map(({ label, count, status, icon: Icon }) => (
          <button
            key={status}
            onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              statusFilter === status ? 'border-gray-800 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-400'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
              <Icon className="w-8 h-8 text-gray-400" />
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search queries by title, user, role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300 focus:border-opci-navy-300"
          />
        </div>
      </div>

      {/* Query List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-opci-navy-500 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading queries...</p>
          </div>
        ) : filteredQueries.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No queries found</p>
            {statusFilter !== 'all' && (
              <button onClick={() => setStatusFilter('all')} className="mt-2 text-opci-navy-500 hover:text-opci-navy-600">
                Show all queries
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredQueries.map((query) => (
              <div key={query.queryId} className="hover:bg-gray-50">
                {/* Query header - always visible */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedQuery(expandedQuery === query.queryId ? null : query.queryId)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${getPriorityColor(query.priority)}`}>
                      <AlertTriangle className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium text-gray-900">{query.title}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {query.messages.length > 0 && (
                            <span className="text-xs text-gray-400">{query.messages.length} msg</span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(query.status)}`}>
                            {query.status}
                          </span>
                          {expandedQuery === query.queryId ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <UserIcon size={12} />
                          To: {getTargetLabel(query)}
                        </span>
                        <span>•</span>
                        <span>{new Date(query.raisedAt).toLocaleDateString()} by {query.raisedByUsername}</span>
                        {query.subject && (
                          <>
                            <span>•</span>
                            <span>Subject: {query.subject.displayId || query.subject.subjectId}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded: message thread + actions */}
                {expandedQuery === query.queryId && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {/* Message thread */}
                    {query.messages.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {query.messages.map((msg, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${msg.authorRole === 'admin' ? 'bg-purple-50 border border-purple-100' : 'bg-blue-50 border border-blue-100'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-gray-900">{msg.authorUsername}</span>
                              <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${getRoleBadge(msg.authorRole)}`}>
                                {msg.authorRole}
                              </span>
                              <span className="text-xs text-gray-400">
                                {new Date(msg.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{msg.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Close reason */}
                    {query.status === 'closed' && query.closeReason && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-sm text-green-800">
                          <span className="font-medium">Closed</span> by {query.closedByUsername} on {query.closedAt && new Date(query.closedAt).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-green-700 mt-1">{query.closeReason}</p>
                      </div>
                    )}

                    {/* Reply box */}
                    {canReply(query) && (
                      <div className="mt-3">
                        {replyingTo === query.queryId ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Type your reply..."
                              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300 text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && replyText.trim()) {
                                  replyMutation.mutate({ queryId: query.queryId, text: replyText });
                                }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                if (replyText.trim()) {
                                  replyMutation.mutate({ queryId: query.queryId, text: replyText });
                                }
                              }}
                              disabled={!replyText.trim() || replyMutation.isPending}
                              className="px-3 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 disabled:opacity-50"
                            >
                              <Send size={16} />
                            </button>
                            <button
                              onClick={() => { setReplyingTo(null); setReplyText(''); }}
                              className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setReplyingTo(query.queryId)}
                              className="px-3 py-1.5 text-sm bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 flex items-center gap-1"
                            >
                              <Send size={14} /> Reply
                            </button>
                            {canClose(query) && (
                              <button
                                onClick={() => { setClosingQuery(query); setShowCloseModal(true); }}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                              >
                                <CheckCircle size={14} /> Close
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Query Modal */}
      {showNewQuery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">New Query</h2>
              <button onClick={() => setShowNewQuery(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => { setNewTargetType('user'); setNewTargetRole(''); setNewSubjectId(''); }}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${newTargetType === 'user' ? 'border-opci-navy-500 bg-opci-navy-50 text-opci-navy-700' : 'border-gray-300'}`}
                  >
                    Specific User
                  </button>
                  <button
                    type="button"
                    onClick={() => { setNewTargetType('role'); setNewTargetUserId(''); setNewSubjectId(''); }}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${newTargetType === 'role' ? 'border-opci-navy-500 bg-opci-navy-50 text-opci-navy-700' : 'border-gray-300'}`}
                  >
                    All Users in Role
                  </button>
                </div>
                {newTargetType === 'user' ? (
                  <select
                    value={newTargetUserId}
                    onChange={(e) => { setNewTargetUserId(e.target.value); setNewSubjectId(''); }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
                  >
                    <option value="">Select a user...</option>
                    {studyUsers.filter(u => u.role !== 'admin').map(u => (
                      <option key={u._id} value={u._id}>{u.fullName} ({u.username}) — {u.role}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={newTargetRole}
                    onChange={(e) => { setNewTargetRole(e.target.value); setNewSubjectId(''); }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
                  >
                    <option value="">Select a role...</option>
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Subject (required, shows after target is selected) */}
              {targetKey && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  {targetSubjects.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No subjects available for this target</p>
                  ) : (
                    <select
                      value={newSubjectId}
                      onChange={(e) => setNewSubjectId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
                    >
                      <option value="">Select a subject...</option>
                      {targetSubjects.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.displayId}{s.site ? ` (${s.site.siteName})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
                  placeholder="Brief description of the query"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <div className="flex gap-2">
                  {(['low', 'medium', 'high'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNewPriority(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg border capitalize ${
                        newPriority === p ? getPriorityColor(p) + ' border-current' : 'border-gray-300 text-gray-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-opci-navy-300"
                  placeholder="Describe what you need..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setShowNewQuery(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuery}
                disabled={!newTitle.trim() || (!newTargetUserId && !newTargetRole) || !newSubjectId || createMutation.isPending}
                className="px-4 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Query'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Query Modal */}
      {showCloseModal && closingQuery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Close Query</h2>
              <button onClick={() => { setShowCloseModal(false); setClosingQuery(null); setCloseReason(''); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{closingQuery.title}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Close Reason</label>
              <textarea
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Reason for closing..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowCloseModal(false); setClosingQuery(null); setCloseReason(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (closeReason.trim()) {
                    closeMutation.mutate({ queryId: closingQuery.queryId, closeReason });
                  }
                }}
                disabled={!closeReason.trim() || closeMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {closeMutation.isPending ? 'Closing...' : <><CheckCircle size={16} /> Close Query</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryManagement;
